/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { Field } from '@src/app/shared/field/Field';
import { FORUM_PRIVS, Forum, ForumMember, ForumRole } from '@src/app/model/forum';
import { PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { maxChars, required, uniqueGrid } from '@src/app/shared/validators';
import { UserService } from '@src/app/modules/user/user.service';
import { ForumService } from '@src/app/modules/social/forum/forum.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { Role } from '@src/app/model/role';
import { ForumPeopleAction } from '@src/app/modules/social/forum/forum-people/forum-people.component';
import { GridRow } from '@src/app/shared/grid/grid-row';
import { FormPillComponent } from '@src/app/shared/form/form-pill/form-pill.component';
import { ChannelColourStates, channelState } from '@src/app/modules/social/channelPillFields';
import { AbstractObject } from '@src/app/model/abstract-object';
import { ForumPageNavRoute } from '@src/app/routes/app-router';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

@Component({
    selector: 'app-forum',
    templateUrl: './forum.component.html',
    styleUrls: ['./forum.component.scss'],
    imports: [FormPageComponent]
})
export class ForumComponent extends AbstractPageComponent {
    dataSvc = inject(ForumService);
    dialog = inject(MatDialog);
    protected userSvc = inject(UserService);
    private currentUserSvc = inject(CurrentUserService);


    roles: Role[] = [];

    pickUserDialogFormConfig: FormConfig = new FormConfig({
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FormTextComponent.make('Name', 'name', { validators: [required, maxChars(20)] }),
                FormTextComponent.make('EMail', 'email'),
            ],
            formLayout: LAYOUT_OPTIONS.singleCol,
        }),
        title: $localize`Users`,
        mode: 'list',
    });

    roleRowFactory(o: ForumRole) {
        const row = [
            FieldMaker.id({ visible: Field.noShow }),
            FormPicklistComponent.make('Role', 'roleId', 'role',
                { items: this.roles }, {
                readonly: (o?.id || o?.role) ? true : false,
                cellOpts: { width: '10em' }
            }
            )
        ]
        FORUM_PRIVS.forEach(p => {
            row.push(FormCheckboxComponent.make(p.name, p.id as string),);
        })
        row.push(FieldMaker.rev());
        row.push(FieldMaker.deleteGridRow());
        return row;
    }

    roleField = new GridField({
        field: { value: 'forumRoles', formColumn: 2, cellOpts: { heading: 'Roles' }, visible: Field.formOnly },
        rowFactory: this.roleRowFactory.bind(this),
        objFactory: () => of(new ForumRole()),
        gridValidator: [uniqueGrid('roleId')]
    });

    /** Keyed on pillColor rather than id so a new channel previews as soon as it has a colour. */
    channelField = FormPillComponent.make($localize`Channel`, 'pillColor', {
        makeOption: (f: Forum) => channelState(f.id, f.name, f.pillColor, f.pillIcon),
        condensed: false,
        tableIconOnly: true,
    }).override({ name: 'channel' });

    colorField = FormPicklistComponent.make($localize`Colour`, 'pillColor', null, {
        items: ChannelColourStates as unknown as AbstractObject[],
        refreshes: [(picked: AbstractObject) => this.refreshChannelPreview({ pillColor: picked?.id as string })],
    }, { visible: Field.formOnly });

    iconField = FormTextComponent.make($localize`Icon`, 'pillIcon', {
        visible: Field.formOnly,
        validators: [maxChars(40)],
        hint: $localize`The name of a Material Symbols icon, e.g. forum - browse them at fonts.google.com/icons`,
        valueChanges: (pillIcon: string) => this.refreshChannelPreview({ pillIcon }),
    });

    visibilityField = FormPicklistComponent.make($localize`Visibility`, 'visibility', null, {
        items: [
            { id: 'public', name: $localize`Public` },
            { id: 'restricted', name: $localize`Restricted` },
            { id: 'confidential', name: $localize`Confidential` },
        ],
    });

    private editing: Forum;

    membersField = new GridField({
        field: { value: 'members', formRow: 2, cellOpts: { heading: 'Members' }, visible: Field.formOnly },
        rowFactory: () => [
            FieldMaker.id({ visible: Field.noShow }),
            FieldMaker.idHolder('userId'),
            FormTextComponent.make('Name', 'user.name', { readonly: true, sendServer: false }),
            FormTextComponent.make('EMail', 'user.email', { readonly: true, sendServer: false }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow(),
        ],
        objFactory: () => {
            const dialogRef = this.dialog.open(PickDialogComponent,
                { data: { config: this.pickUserDialogFormConfig, service: this.userSvc } });
            return dialogRef.afterClosed().pipe(first()).pipe<ForumMember>(
                map(user => {
                    console.log('back', user);
                    if (user) {
                        const newItem = new ForumMember();
                        newItem.userId = user.id;
                        newItem.user = user;
                        return newItem;
                    } else {
                        //return null;
                    }
                })
            );
        },
        gridValidator: [uniqueGrid('userId')]
    });

    config = new FormConfig({
        navRoute: ForumPageNavRoute,
        docPath: 'guides/channels',
        title: $localize`Channel`,
        help: $localize`Discussion forums and their membership of who can see and post in them.`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FieldMaker.nameControl({ validators: [required, maxChars(30)] }),
                this.channelField,
                this.colorField,
                this.iconField,
                this.visibilityField,
                FieldMaker.notes(),
                this.roleField,
                //this.membersField,
            ],
            formLayout: [{ cells: [{ width: '33%' }, { width: '66%' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newForum.bind(this),
        beforeEdit: (o: Forum) => {
            this.editing = o;
            this.applyStandardChannelLocks(o);
            return o;
        },
        beforeSave: this.beforeSave.bind(this),
        actions: [new ForumPeopleAction()],
        configReady: this.configReady,
    });

    constructor() {
        const roleSvc = inject(RoleService);

        super();
        roleSvc.get(true).subscribe((roles: Role[]) => {
            this.roles = roles;
            this.configReady.next();
        });
    }

    /** Only support may change a standard channel. */
    private applyStandardChannelLocks(forum: Forum) {
        const lockedToSupport = !!forum.sysRole && !this.currentUserSvc.hasUber();
        this.config.readonly = !this.config.allowEdit || lockedToSupport;
    }

    private refreshChannelPreview(change: Partial<Forum>) {
        this.channelField.setValue(new Forum({
            id: this.editing?.id,
            name: this.editing?.name,
            pillColor: this.colorField.control?.value,
            pillIcon: this.iconField.control?.value,
            ...change,
        }));
    }

    newForum() {
        const f = new Forum();
        this.roles.forEach(r => {
            const fr = new ForumRole();
            fr.roleId = r.id;
            fr.role = r;
            if (r.isAdmin) {
                FORUM_PRIVS.map(f => fr[f.id] = true);
            }
            f.forumRoles.push(fr);
        })
        this.editing = f;
        this.applyStandardChannelLocks(f);
        return of(f);
    }

    beforeSave() {
        const rows = this.roleField.control.gridRows();
        const toDelete: GridRow[] = [];

        rows.forEach(row => {
            let privCnt = 0;
            FORUM_PRIVS.forEach(p => {
                if (row.get(p.id as string).value) {
                    privCnt += 1;
                }
            })

            if (privCnt === 0) {
                toDelete.push(row);
            }
        })
        // directly deleting new rows removes them from grid, making forEach above skip over entries...
        toDelete.forEach(gr => gr.delete());
    }
}
